import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getAdminToken, getPresenceToken } from '../../../api/direct-messages';
import { useSelector } from 'react-redux';
import { createChatClient, isTokenRejected } from '../../../utils/chatClient';

const ConversationListContext = createContext(null);

export function useConversationList() {
  const context = useContext(ConversationListContext);
  if (!context)
    throw new Error(
      'useConversationList must be inside PubnubConversationListProvider'
    );
  return context;
}

// Token chat-server sống ~24h. Hết hạn thì WebSocket đóng với mã 4401 và SDK
// KHÔNG tự kết nối lại (mọi lỗi khác thì có). Tab admin hay mở cả ngày nên phải
// tự xin token mới rồi dựng client mới — SDK không có `setAuthKey`.
const MAX_TOKEN_REFRESH = 3;

/**
 * Mở một kết nối chat-server tự lấy lại token khi bị từ chối.
 *
 * @returns hàm dọn dẹp, gọi được nhiều lần.
 */
function connectWithTokenRefresh({ myId, mintToken, channels, withPresence, listener, onReady }) {
  let client = null;
  let stopped = false;
  let refreshCount = 0;

  const start = async () => {
    if (stopped) return;
    let token;
    try {
      const res = await mintToken(myId);
      token = res?.token;
    } catch (error) {
      console.error('chat-server: không xin được token', error);
      return;
    }
    if (!token || stopped) return;

    try {
      client = createChatClient({ token, userId: myId });
    } catch (error) {
      console.error('chat-server: không tạo được client', error);
      return;
    }

    client.addListener({
      ...listener,
      status: (event) => {
        if (isTokenRejected(event) && !stopped) {
          if (refreshCount >= MAX_TOKEN_REFRESH) {
            console.error('chat-server: token bị từ chối quá nhiều lần, dừng thử lại');
            return;
          }
          refreshCount += 1;
          // instance cũ không dùng lại được sau khi bị từ chối
          client?.disconnect();
          client = null;
          start();
        }
      },
    });
    client.subscribe({ channels, withPresence: withPresence === true });
    onReady?.(client);
  };

  start();

  return () => {
    stopped = true;
    client?.disconnect();
    client = null;
  };
}

export function PubnubConversationListProvider({ children }) {
  const authUser = useSelector((state) => state.auth.authUser);
  const location = useLocation();
  const myId = authUser?.user?.id;
  const isDirectMessagesRoute = Boolean(
    location?.pathname?.startsWith('/app/direct-messages') ||
      (typeof window !== 'undefined' &&
        window.location?.pathname?.startsWith('/app/direct-messages'))
  );
  const [pubnub, setPubnub] = useState(null);
  const [globalUnread, setGlobalUnread] = useState({});
  const [presence, setPresence] = useState({});
  // Số nơi ngoài màn direct-messages đang cần presence (vd. CareChatDrawer).
  // Đếm theo ref-count để nhiều drawer mở cùng lúc không tắt nhầm của nhau.
  const [presenceRequests, setPresenceRequests] = useState(0);
  const presenceEnabled = presenceRequests > 0;

  /**
   * Bật kênh presence khi đang ở ngoài màn direct-messages.
   *
   * @returns hàm nhả, gọi được nhiều lần (chỉ tính lần đầu).
   */
  const requestPresence = useCallback(() => {
    let released = false;
    setPresenceRequests((n) => n + 1);
    return () => {
      if (released) return;
      released = true;
      setPresenceRequests((n) => Math.max(0, n - 1));
    };
  }, []);

  const [conversations, setConversations] = useState([]);

  // Kênh presence chung: ai đang online.
  useEffect(() => {
    if (!myId || !(isDirectMessagesRoute || presenceEnabled)) return undefined;

    const disconnect = connectWithTokenRefresh({
      myId,
      mintToken: getPresenceToken,
      channels: ['presence_global'],
      withPresence: true,
      listener: {
        presence: (event) => {
          const uid = parseInt(String(event.uuid), 10);
          if (isNaN(uid)) return;
          setPresence((prev) => ({
            ...prev,
            [uid]: {
              isOnline: event.action === 'join',
              lastSeenAt:
                event.action === 'leave' || event.action === 'timeout'
                  ? new Date()
                  : prev[uid]?.lastSeenAt,
            },
          }));
        },
      },
      onReady: (client) => {
        // Danh sách người đang online lúc vừa vào. hereNow giờ trả Promise.
        client
          .hereNow({ channels: ['presence_global'], includeUUIDs: true })
          .then((res) => {
            const occupants = res?.channels?.presence_global?.occupants || [];
            const initialPresence = {};
            occupants.forEach((o) => {
              const uid = parseInt(o.uuid, 10);
              if (!isNaN(uid)) initialPresence[uid] = { isOnline: true };
            });
            setPresence(initialPresence);
          })
          .catch((error) => console.error('chat-server hereNow lỗi:', error));
      },
    });
    return () => {
      disconnect();
      setPresence({});
    };
  }, [myId, isDirectMessagesRoute, presenceEnabled]);

  // Kênh riêng của tài khoản: cập nhật danh sách hội thoại + số tin chưa đọc.
  useEffect(() => {
    if (!myId || !isDirectMessagesRoute) return undefined;

    const disconnect = connectWithTokenRefresh({
      myId,
      mintToken: getAdminToken,
      channels: [`user_${myId}_updates`],
      listener: {
        message: (event) => {
          const { type, data } = event.message || {};

          if (type === 'channel_list_update' && data?.channel_id) {
            setConversations((prev) => {
              const exists = prev.some((c) => c.channel_id === data.channel_id);

              if (data.last_message) {
                const updatedConversation = exists
                  ? { ...prev.find((c) => c.channel_id === data.channel_id) }
                  : {
                      channel_id: data.channel_id,
                      channel_type: data.channel_type || 'one_to_one',
                      last_message: {
                        message: { content: '', type: '' },
                        user_info: { name: '', user_id: 0, avatar: '' },
                        sender_id: 0,
                        timetoken: 0,
                      },
                      unread_count: 0,
                    };
                updatedConversation.last_message = {
                  message: {
                    content: data.last_message.content,
                    type: data.last_message.type,
                  },
                  user_info: {
                    name: data.last_message.sender_name,
                    avatar:
                      updatedConversation.last_message?.user_info?.avatar || '',
                    user_id: data.last_message.sender_id,
                  },
                  sender_id: data.last_message.sender_id,
                  timetoken: data.last_message.timetoken,
                };
                updatedConversation.unread_count = data.unread_count || 0;

                const newList = exists
                  ? prev.map((c) =>
                      c.channel_id === data.channel_id ? updatedConversation : c
                    )
                  : [updatedConversation, ...prev];

                return newList.sort(
                  (a, b) =>
                    Number(b.last_message?.timetoken || 0) -
                    Number(a.last_message?.timetoken || 0)
                );
              }

              if (exists) {
                return prev.map((c) =>
                  c.channel_id === data.channel_id
                    ? {
                        ...c,
                        unread_count: data.unread_count,
                        updated_at: data.updated_at,
                      }
                    : c
                );
              }

              return prev;
            });
          }

          if (type === 'global_unread_update') {
            setGlobalUnread(data);
          }
        },
      },
      onReady: setPubnub,
    });
    return () => {
      disconnect();
      setPubnub(null);
    };
  }, [myId, isDirectMessagesRoute]);

  return (
    <ConversationListContext.Provider
      value={{
        pubnub,
        conversations,
        setConversations,
        presence,
        globalUnread,
        requestPresence,
      }}
    >
      {children}
    </ConversationListContext.Provider>
  );
}
